import { useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Save, Loader2, CheckCircle2, Calendar, AlertCircle, Info, Check, RefreshCw } from "lucide-react";
import { ActivityType, GeneralDataFormProps } from "@/types/general-data";
import { useGeneralData } from "@/components/footprints/hooks/useGeneralData";
import { ConfidentialityDisclaimer } from "@/components/footprints/ConfidentialityDisclaimer";

export const GeneralDataStep = ({
  year,
  existingData,
  onDataChange,
  onComplete,
  onIncomplete,
  onSaveDraft,
  isSavingDraft,
  hasUnsavedChanges,
  lastSaved,
}: GeneralDataFormProps) => {
  const { t } = useLanguage();

  // Use ref to store the latest onDataChange callback to avoid dependency issues
  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  const {
    // Data
    localData,
    company,
    activeStoneTypes,

    // Loading states
    companyLoading,
    stoneTypesLoading,
    isUpdatingActivityType,

    // Error states
    companyError,
    stoneTypesError,
    fieldErrors,

    // Activity type state
    selectedActivityType,

    // Handlers
    handleInputChange,
    handleInputBlur,
    handleSelectBlur,
    handleActivityTypeChange,
    handleSaveActivityType,

    // Utilities
    getInputClassName,
    getFieldError,
    checkCompletion,
  } = useGeneralData(year, existingData);

  // Update form data whenever local data changes - use ref to avoid infinite loops
  useEffect(() => {
    onDataChangeRef.current(localData);
  }, [localData]);

  // Check completion status
  useEffect(() => {
    checkCompletion((company?.activityType as ActivityType) || selectedActivityType, onComplete, onIncomplete);
  }, [localData, company?.activityType, selectedActivityType, fieldErrors, onComplete, onIncomplete, checkCompletion]);

  const renderFieldError = (field: string) => {
    const error = getFieldError(field);
    if (!error) return null;

    return (
      <div className="flex items-center gap-1 text-red-500 text-sm mt-1">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  };

  if (companyLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="text-center text-gray-500">{t("common.loading")}...</div>
        </div>
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="text-center text-red-500">{t("error.companyDataNotFound")}</div>
        </div>
      </div>
    );
  }

  const activityType = company.activityType;

  return (
    <div className="w-full max-w-5xl mx-auto">
      <ConfidentialityDisclaimer />
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-gray-700">{t("footprints.generalData.title")}</h2>
          <div className="flex items-center text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200 text-xs font-medium">
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            <span>{year}</span>
          </div>
        </div>

        {!activityType ? (
          <div className="space-y-8">
            {/* Informational Header */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Info className="h-6 w-6 text-amber-600 mt-0.5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-amber-800 mb-2">
                    {t("footprints.generalData.activityTypeRequired")}
                  </h3>
                  <p className="text-amber-700 text-sm leading-relaxed mb-4">
                    {t("footprints.generalData.activityTypeRequiredInlineDescription")}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-3">
                      {t("footprints.generalData.activityTypeOptions")}:
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="font-medium text-gray-700">{t("activityTypes.extraction")}</p>
                          <p className="text-sm text-gray-600">{t("footprints.generalData.extractionDescription")}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="font-medium text-gray-700">{t("activityTypes.transformation")}</p>
                          <p className="text-sm text-gray-600">
                            {t("footprints.generalData.transformationDescription")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <p className="font-medium text-gray-700">
                            {t("activityTypes.extraction_and_transformation")}
                          </p>
                          <p className="text-sm text-gray-600">
                            {t("footprints.generalData.extractionAndTransformationDescription")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Type Selection */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="activityTypeSelect" className="text-base font-medium">
                    {t("footprints.generalData.activityType")} *
                  </Label>
                  <Select value={selectedActivityType} onValueChange={handleActivityTypeChange}>
                    <SelectTrigger id="activityTypeSelect" className="h-12">
                      <SelectValue placeholder={t("footprints.generalData.selectActivityType")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="extraction">{t("activityTypes.extraction")}</SelectItem>
                      <SelectItem value="transformation">{t("activityTypes.transformation")}</SelectItem>
                      <SelectItem value="extraction_and_transformation">
                        {t("activityTypes.extraction_and_transformation")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleSaveActivityType}
                  disabled={!selectedActivityType || isUpdatingActivityType}
                  className="bg-teal-600 hover:bg-teal-700 text-white h-12 w-full"
                >
                  {isUpdatingActivityType ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("common.saving")}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {t("footprints.generalData.saveActivityType")}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Next Steps Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">{t("footprints.generalData.nextStepsTitle")}</p>
                  <p className="text-xs text-blue-700 mt-1">{t("footprints.generalData.nextStepsDescription")}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Activity Type Confirmation */}
            <div className="mb-6">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-teal-700">
                    {t("footprints.generalData.activityType")}: {t(`activityTypes.${activityType}`)}
                  </p>
                  <p className="text-xs text-teal-600 mt-1">{t("footprints.workingWithYear", { year })}</p>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-8">
              {(activityType === "extraction" || activityType === "extraction_and_transformation") && (
                <div>
                  <h3 className="text-base font-medium text-gray-800 mb-4">
                    {t("footprints.generalData.extractionData")}
                  </h3>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="stoneTypeId">{t("footprints.generalData.stoneType")} *</Label>
                      <SearchableSelect
                        value={localData.stoneTypeId || ""}
                        onChange={(value) => {
                          handleInputChange("stoneTypeId", value);
                          // Clear any existing error immediately when a value is selected
                          if (value && fieldErrors["stoneTypeId"]) {
                            handleInputBlur("stoneTypeId", value, true);
                          }
                        }}
                        onBlur={(value) => handleSelectBlur("stoneTypeId", value, true)}
                        options={activeStoneTypes.map((stoneType) => ({
                          value: stoneType.id,
                          label: stoneType.name,
                        }))}
                        placeholder={
                          stoneTypesLoading
                            ? t("common.loading")
                            : activeStoneTypes.length === 0
                            ? t("footprints.generalData.noStoneTypesAvailable")
                            : t("footprints.generalData.selectStoneType")
                        }
                        disabled={stoneTypesLoading}
                        loading={stoneTypesLoading}
                        className={getInputClassName("stoneTypeId", "")}
                      />
                      {renderFieldError("stoneTypeId")}
                      {stoneTypesError && (
                        <p className="text-sm text-red-500">{t("footprints.generalData.stoneTypesLoadError")}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="prod_ext">{t("footprints.generalData.totalExtractionProduction")} *</Label>
                        <div className="relative">
                          <Input
                            id="prod_ext"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("prod_ext", "h-12 pr-10")}
                            value={localData.prod_ext || ""}
                            onChange={(e) => handleInputChange("prod_ext", e.target.value)}
                            onBlur={(e) => handleInputBlur("prod_ext", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            t
                          </span>
                        </div>
                        {renderFieldError("prod_ext")}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="fat_ext">{t("footprints.generalData.totalExtractionRevenue")} *</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-gray-500 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-md">
                                <p>{t("footprints.generalData.revenueAllocationTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="relative">
                          <Input
                            id="fat_ext"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("fat_ext", "h-12 pr-10")}
                            value={localData.fat_ext || ""}
                            onChange={(e) => handleInputChange("fat_ext", e.target.value)}
                            onBlur={(e) => handleInputBlur("fat_ext", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            €
                          </span>
                        </div>
                        {renderFieldError("fat_ext")}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pvp_caro_ext">{t("footprints.generalData.expensiveProductPVP")} *</Label>
                        <div className="relative">
                          <Input
                            id="pvp_caro_ext"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("pvp_caro_ext", "h-12 pr-14")}
                            value={localData.pvp_caro_ext || ""}
                            onChange={(e) => handleInputChange("pvp_caro_ext", e.target.value)}
                            onBlur={(e) => handleInputBlur("pvp_caro_ext", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            €/t
                          </span>
                        </div>
                        {renderFieldError("pvp_caro_ext")}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="pvp_barato_ext">{t("footprints.generalData.cheapProductPVP")} *</Label>
                        <div className="relative">
                          <Input
                            id="pvp_barato_ext"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("pvp_barato_ext", "h-12 pr-14")}
                            value={localData.pvp_barato_ext || ""}
                            onChange={(e) => handleInputChange("pvp_barato_ext", e.target.value)}
                            onBlur={(e) => handleInputBlur("pvp_barato_ext", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            €/t
                          </span>
                        </div>
                        {renderFieldError("pvp_barato_ext")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(activityType === "transformation" || activityType === "extraction_and_transformation") && (
                <div>
                  <h3 className="text-base font-medium text-gray-800 mb-4">
                    {t("footprints.generalData.transformationData")}
                  </h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="prod_trsf">{t("footprints.generalData.totalTransformationProduction")} *</Label>
                        <div className="relative">
                          <Input
                            id="prod_trsf"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("prod_trsf", "h-12 pr-10")}
                            value={localData.prod_trsf || ""}
                            onChange={(e) => handleInputChange("prod_trsf", e.target.value)}
                            onBlur={(e) => handleInputBlur("prod_trsf", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            t
                          </span>
                        </div>
                        {renderFieldError("prod_trsf")}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="fat_trsf">{t("footprints.generalData.totalTransformationRevenue")} *</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-gray-500 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-md">
                                <p>{t("footprints.generalData.revenueAllocationTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="relative">
                          <Input
                            id="fat_trsf"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("fat_trsf", "h-12 pr-10")}
                            value={localData.fat_trsf || ""}
                            onChange={(e) => handleInputChange("fat_trsf", e.target.value)}
                            onBlur={(e) => handleInputBlur("fat_trsf", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            €
                          </span>
                        </div>
                        {renderFieldError("fat_trsf")}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pvp_caro_trsf">
                          {t("footprints.generalData.expensiveProductPVPTransformation")} *
                        </Label>
                        <div className="relative">
                          <Input
                            id="pvp_caro_trsf"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("pvp_caro_trsf", "h-12 pr-14")}
                            value={localData.pvp_caro_trsf || ""}
                            onChange={(e) => handleInputChange("pvp_caro_trsf", e.target.value)}
                            onBlur={(e) => handleInputBlur("pvp_caro_trsf", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            €/t
                          </span>
                        </div>
                        {renderFieldError("pvp_caro_trsf")}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="pvp_barato_trsf">
                          {t("footprints.generalData.cheapProductPVPTransformation")} *
                        </Label>
                        <div className="relative">
                          <Input
                            id="pvp_barato_trsf"
                            type="number"
                            step="0.01"
                            min="0"
                            className={getInputClassName("pvp_barato_trsf", "h-12 pr-14")}
                            value={localData.pvp_barato_trsf || ""}
                            onChange={(e) => handleInputChange("pvp_barato_trsf", e.target.value)}
                            onBlur={(e) => handleInputBlur("pvp_barato_trsf", parseFloat(e.target.value) || 0, true)}
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                            €/t
                          </span>
                        </div>
                        {renderFieldError("pvp_barato_trsf")}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="rend">{t("footprints.generalData.transformationProcessYield")} *</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-500 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm">
                              <p>{t("footprints.generalData.yieldNote")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="relative">
                        <Input
                          id="rend"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          className={getInputClassName("rend", "h-12 pr-10")}
                          value={localData.rend || ""}
                          onChange={(e) => handleInputChange("rend", e.target.value)}
                          onBlur={(e) => handleInputBlur("rend", parseFloat(e.target.value) || 0, true)}
                          placeholder="0.00"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none z-10">
                          %
                        </span>
                      </div>
                      {renderFieldError("rend")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Save Draft Button */}
        {hasUnsavedChanges && onSaveDraft && activityType && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <AlertCircle className="h-4 w-4" />
                  <span>{t("footprints.generalData.unsavedChanges")}</span>
                </div>
                {lastSaved && (
                  <div className="text-xs text-gray-500">
                    {t("footprints.generalData.lastSaved")}:{" "}
                    {(() => {
                      // Convert Firestore timestamp to Date if needed
                      let date = lastSaved;
                      if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                        date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                      }
                      return date instanceof Date
                        ? date.toLocaleString("pt-PT")
                        : t("footprints.generalData.invalidDate");
                    })()}
                  </div>
                )}
              </div>
              <Button
                onClick={onSaveDraft}
                disabled={isSavingDraft}
                variant="outline"
                size="sm"
                className="border-teal-200 text-teal-600 hover:bg-teal-50"
              >
                {isSavingDraft ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t("footprints.generalData.saving")}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t("footprints.generalData.saveDraft")}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
